import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  IconButton,
  Text,
  Collapse,
  Portal,
  useDisclosure,
  Flex,
  Badge,
  Avatar,
  Icon,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FiMenu,
  FiX,
  FiHome,
  FiUsers,
  FiUser,
  FiCalendar,
  FiDollarSign,
  FiLogOut,
  FiShield,
  FiUserPlus,
  FiChevronRight,
  FiGrid,
  FiMail,
  FiDatabase,
  FiExternalLink,
  FiSun,
  FiMoon,
  FiFileText,
  FiGift,
  FiBookOpen,
  FiVideo,
  FiUserCheck,
} from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginWithSmsModal } from '../pages/authentication';
import { getColor, brandConfig } from '../brandConfig';
import { useShiftKey, isDevMode } from '../hooks/useShiftKey';
import { renderPermissionTooltip } from './devPermissionTooltip';

// Import module configs
import clientsModuleConfig from '../pages/clients/moduleConfig';
import governanceModuleConfig from '../pages/governance/moduleConfig';
import { calendarsModuleConfig } from '../pages/calendars/moduleConfig';
import billsModuleConfig from '../pages/bills/moduleConfig';
import emailsModuleConfig from '../pages/emails/moduleConfig';
import opportunitiesModuleConfig from '../pages/opportunities/moduleConfig';
import companyKnowledgeModuleConfig from '../pages/companyknowledge/moduleConfig';
import lmModuleConfig from '../pages/lm/moduleConfig';
import mediaCDNModuleConfig from '../pages/media-cdn/moduleConfig';
import pModuleConfig from '../pages/p/moduleConfig';
import rewardsModuleConfig from '../pages/rewards/moduleConfig';
import prospectingModuleConfig from '../pages/prospecting/moduleConfig';
import employeesModuleConfig from '../pages/employees/moduleConfig';
import shiftsModuleConfig from '../pages/shifts/moduleConfig';
import companiesModuleConfig from '../pages/companies/moduleConfig';
import digitalManagersModuleConfig from '../pages/digital-managers/moduleConfig';
import policiesModuleConfig from '../pages/policies/moduleConfig';
import meetModuleConfig from '../pages/meet/moduleConfig';
import { useNavbarState } from '../hooks/useNavbarState';
import { useDraggableFloat, computeMenuPlacement } from '../hooks/useDraggableFloat';
import { useHideToEdge } from '../hooks/useHideToEdge';
import { RESET_FLOATING_PANELS } from '../hooks/floatingPanels';
import {
  LAUNCHER, menuMaxH, LauncherEdgeTab, LauncherHideButton, useLauncherClickOutside,
  launcherFabMotion, useLauncherReducedMotion,
} from './floating/LauncherChrome';

/**
 * FloatingNavbar — the app-wide navigation launcher.
 *
 * Converted 2026-09-03 from the template's fixed `bottom:20 left:20` container-anchored layout
 * to the draggable standard (vault/how-to/draggable-floating-launchers.md, "Surgical sync
 * procedure"): `useDraggableFloat` for the FAB, the menu in its OWN Portal placed by
 * `computeMenuPlacement` with `menuMaxH` so it always opens fully on screen, hide-to-edge tab,
 * the reset broadcast with self-clearing keys, every size from `LauncherChrome`, and the
 * shadow/motion helpers (no idle animation; `prefers-reduced-motion` honoured).
 * Nav items, categories and branding logic are this repo's own and were left untouched.
 *
 * Every piece of state is persisted per user in localStorage under the `tlp_` tenant prefix
 * (registered in hooks/floatingPanels.ts — never rename a live key):
 *   tlp_floating_navbar_pos_<clientId|guest>     — FAB position ({left, top})
 *   tlp_floating_navbar_hidden_<clientId|guest>  — hidden-to-edge flag ("1"/"0")
 *   tlp_floating_navbar_state-<clientId>         — open / active category / expanded modules (useNavbarState)
 */

// Every size from LauncherChrome (doc § One file owns the numbers).
const BUTTON_SIZE = LAUNCHER.FAB_SIZE;
const EDGE_PAD = LAUNCHER.EDGE_PAD;

/**
 * Home corner (doc § Default corners): bottom-LEFT on desktop — the corner the fixed layout used,
 * so nobody sees a jump on first load. Phones put it bottom-RIGHT, above the iOS home indicator,
 * to keep it off calendar / roster grids. The module-nav launcher owns the left edge's vertical
 * centre and Tom AI the bottom-right corner, so nothing stacks on a first-run session.
 * The reset handler re-computes THIS function — never a hand-typed fallback.
 */
const navHome = (vw: number, vh: number) => {
  const isMobile = vw < 768;
  if (isMobile) {
    return { left: vw - BUTTON_SIZE - 16, top: vh - BUTTON_SIZE - 96 };
  }
  return { left: 20, top: vh - BUTTON_SIZE - 20 };
};

// z-index ladder (doc § Reset-panels broadcast + the z-index ladder). The site navbar is
// Chakra `sticky` (1100); a launcher that must stay clickable wherever it is dragged sits ABOVE
// it (the 2026-08-30 finding), and deliberately BELOW the 1300 immersive overlays.
const Z_FAB = 1101;
const Z_MENU = 1102;
// The emergency-logout mini-FAB tracks the main FAB, so it shares the FAB's layer — at the
// ladder's original 997 it went under the sticky navbar whenever the FAB was parked at the top.
const Z_LOGOUT = 1101;

/**
 * Routes where the app-wide launcher must not exist at all (doc § Suppressing launchers):
 * candidate-facing / public pages a logged-in recruiter may open to preview. Checked AFTER every
 * hook so hook order is stable across renders.
 */
const SUPPRESSED_ROUTE_PATTERNS: RegExp[] = [
  /^\/login/,                       // sign-in page
  /^\/prospecting\/form\//,         // public candidate pre-screening form
  /^\/prospecting\/chat\//,         // public candidate chat
  /^\/prospecting\/reference\//,    // public referee form (token link)
  /^\/book\//,                      // public booking pages
  /^\/calendars\/booking\/manage\//,// public booking management (token link)
  /^\/subscribe\//,                 // public subscription landing page
];

// Menu open animation — a single slide when the panel opens, never an idle loop
// (doc § Motion: the only self-started motion is the edge tab's announce).
const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

// Icon mapping for modules
export const moduleIcons: Record<string, any> = {
  profile: FiUser,
  clients: FiUsers,
  calendars: FiCalendar,
  bills: FiDollarSign,
  opportunities: FiDollarSign,
  emails: FiMail,
  companyknowledge: FiDatabase,
  'lm': FiDatabase,
  'media-cdn': FiDatabase,
  'p': FiFileText,
  'rewards': FiGift,
  policies: FiBookOpen,
  meet: FiVideo,
  prospecting: FiUserCheck,
};

// AdminModuleLinks on the home page derives its quick-link grid from the SAME
// canonical list: a module registered here gets its home button for free.
export const allModuleConfigs = [
  clientsModuleConfig,
  calendarsModuleConfig,
  opportunitiesModuleConfig,
  pModuleConfig,
  billsModuleConfig,
  emailsModuleConfig,
  companyKnowledgeModuleConfig,
  lmModuleConfig,
  mediaCDNModuleConfig,
  rewardsModuleConfig,
  prospectingModuleConfig,
  employeesModuleConfig,
  shiftsModuleConfig,
  companiesModuleConfig,
  digitalManagersModuleConfig,
  policiesModuleConfig,
  meetModuleConfig,
  governanceModuleConfig,
];

interface FloatingNavbarProps {
  modules?: any[]; // Resolved modules from App.tsx with backend permissions
}

export const FloatingNavbar: React.FC<FloatingNavbarProps> = ({ modules: resolvedModules }) => {
  // Use persistent state management with client-specific storage
  const { isAuthenticated, user, logout, refreshAuth } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  const location = useLocation();
  // Use client ID for user-specific navbar state
  const clientId = user?.clientId || user?.client?.id || user?.id;


  const {
    isExpanded,
    setIsExpanded,
    activeCategory,
    setActiveCategory,
    expandedModules,
    toggleModuleExpanded,
    resetState,
  } = useNavbarState(clientId);

  // Per-user draggable launcher position + hide-to-edge flag (persisted, tlp_ prefix).
  const posKey = `tlp_floating_navbar_pos_${clientId || 'guest'}`;
  const hiddenKey = `tlp_floating_navbar_hidden_${clientId || 'guest'}`;
  const { position, setPosition, dragHandlers, wasJustDragged } = useDraggableFloat({
    storageKey: posKey,
    size: BUTTON_SIZE,
    edgePad: EDGE_PAD,
    defaultPosition: navHome,
  });
  const { hidden: navHidden, setHidden: setNavHidden, justHidden: navJustHidden } = useHideToEdge(hiddenKey);
  const reducedMotion = useLauncherReducedMotion();

  // The reset failsafe (footer's "↻ Reset floating panels"). Self-clearing is REQUIRED: the hook
  // persists only on drag-END, so a listener that merely moved the button would let the lost
  // position come back on the next reload.
  useEffect(() => {
    const onReset = () => {
      setIsExpanded(false);
      setActiveCategory(null);
      setPosition(navHome(window.innerWidth, window.innerHeight));
      try {
        localStorage.removeItem(posKey);
        localStorage.removeItem(hiddenKey);
      } catch { /* private browsing */ }
    };
    window.addEventListener(RESET_FLOATING_PANELS, onReset);
    return () => window.removeEventListener(RESET_FLOATING_PANELS, onReset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posKey, hiddenKey]);

  // Viewport-safe placement for the menu panel — it renders in its OWN Portal at viewport
  // coordinates, never inside the FAB's 56px container (the container-anchor trap).
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const panelPlacement = computeMenuPlacement(
    { left: position.left, top: position.top, size: BUTTON_SIZE },
    { w: viewportW, h: viewportH },
    { preferredWidth: Math.max(400, LAUNCHER.MENU_W), estimatedHeight: 480, gap: LAUNCHER.MENU_GAP },
    EDGE_PAD,
  );


  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();
  const navigate = useNavigate();

  // Dev-mode Shift+hover permission reveal — see vault/how-to/dev-mode-permission-hover.md
  const shiftHeld = useShiftKey();
  const devReveal = isDevMode() && shiftHeld;

  // Refresh auth data when component mounts and user is authenticated but data is missing
  useEffect(() => {

    if (isAuthenticated && (!user || !user.permissions)) {
      refreshAuth();
    }
  }, [isAuthenticated, user, refreshAuth]);


  // Merge resolved permissions from backend (via App.tsx) into internal module list.
  // Modules migrated to single-source-of-truth have empty permission arrays in their config
  // and get real permissions resolved from the backend at runtime.
  const mergedModuleConfigs = React.useMemo(() => {
    if (!resolvedModules) return allModuleConfigs;
    const resolvedMap = new Map(resolvedModules.map((m: any) => [m.id, m]));
    return allModuleConfigs.map(mod => {
      const resolved = resolvedMap.get(mod.id);
      if (!resolved) return mod; // Not in App.tsx's list — keep as-is
      return { ...mod, permissions: resolved.permissions, navigation: resolved.navigation };
    });
    // allModuleConfigs is rebuilt from module-scope constants every render — not a real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedModules]);

  // Filter modules based on user permissions
  const getAvailableModules = () => {
    // console.log('📦 [4] FloatingNavbar - Getting available modules', {
    //   totalModules: allModuleConfigs.length,
    //   hasUser: !!user,
    //   userPermissions: user?.permissions || [],
    // });

    if (!user || !user.permissions || user.permissions.length === 0) {
      return [];
    }

    // ADMIN and TENANT_MASTER_ADMIN bypass all per-module view-permission
    // checks — they should see every enabled module on the tenant without
    // needing specific VIEW perms per module. Matches the pattern referenced
    // in the monorepo's CLAUDE.md memory about FloatingNavbar ADMIN bypass.
    const isSuperAdmin = user.permissions.includes('ADMIN')
      || user.permissions.includes('TENANT_MASTER_ADMIN');
    if (isSuperAdmin) {
      return mergedModuleConfigs;
    }

    const filteredModules = mergedModuleConfigs.filter(module => {
      const hasPermission = user?.permissions?.some((permission: string) => {
        // Check for view permissions in the standard format
        const hasViewPermission = module.permissions?.view?.includes(permission) || false;
        return hasViewPermission;
      }) || false;

      return hasPermission;
    });


    return filteredModules;
  };

  // Group modules by category — hardcoded allowlist (NOT driven from the
  // module's `category` field; modules not listed here simply don't render).
  const categorizedModules = {
    'Recruitment': ['prospecting', 'employees', 'shifts', 'companies', 'policies', 'meet'],
    'Automation': ['digital-managers'],
    'Business': ['clients', 'opportunities', 'p', 'bills'],
    'Communication': ['calendars', 'emails'],
    'Content': ['companyknowledge', 'lm', 'media-cdn'],
    'Rewards': ['rewards'],
    'Settings': ['profile'],
  };

  const availableModules = isAuthenticated ? getAvailableModules() : [];


  const handleNavItemClick = (path: string) => {
    navigate(path);
    // Keep the menu state but close the expanded view
    setIsExpanded(false);
  };

  const handleOpenInNewTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsExpanded(false);
    // Reset navbar state on logout
    resetState();
  };

  // Filter navigation items based on user permissions
  const getVisibleNavItems = (navigation: any) => {
    if (!navigation) return [];

    // Handle different navigation formats
    let navItems: any[] = [];
    if (Array.isArray(navigation)) {
      navItems = navigation;
    } else if (navigation.sub && Array.isArray(navigation.sub)) {
      navItems = navigation.sub;
    }

    return navItems.filter((item: any) => {
      // If no permissions required, show to everyone
      if (!item.permissions || item.permissions.length === 0) return true;

      // Check if user has required permission
      const hasPermission = user?.permissions?.some((permission: string) =>
        item.permissions.includes(permission)
      ) || false;

      // Debug logging for merge clients
      if (item.path === '/clients/merge') {
        console.log('🔍 [NAV-DEBUG] Merge Clients check:', {
          itemPermissions: item.permissions,
          userPermissions: user?.permissions,
          hasPermission
        });
      }

      return hasPermission;
    });
  };

  // Close menu when tapping outside the FAB, the menu panel, or the emergency-logout mini-FAB
  // — three Portals, three classNames to exempt. pointerdown-based (LauncherChrome) so a
  // finger tap outside closes it too.
  const closeMenu = useCallback(() => {
    setIsExpanded(false);
    setActiveCategory(null);
  }, [setIsExpanded, setActiveCategory]);
  useLauncherClickOutside(
    isExpanded,
    ['.floating-navbar-container', '.floating-navbar-menu', '.floating-navbar-logout'],
    closeMenu,
  );

  // Route suppression — after every hook (doc § Suppressing launchers).
  if (SUPPRESSED_ROUTE_PATTERNS.some((rx) => rx.test(location.pathname))) return null;

  // Hidden: the edge tab, and NOTHING else. Rendering nothing for a hidden NAVIGATION launcher
  // strands the user with no way to move around the app — the tab is never gated.
  if (navHidden) {
    return (
      <Portal>
        <LauncherEdgeTab
          announce={navJustHidden}
          edge="left"
          top={position.top}
          bg={getColor("secondary", colorMode)}
          zIndex={Z_FAB}
          label="Bring the navigation menu back"
          onClick={() => setNavHidden(false)}
        />
      </Portal>
    );
  }

  return (
    <>
      {/* ═══ Portal A — MENU PANEL (own portal, viewport-fixed via computeMenuPlacement) ═══ */}
      <Portal>
        <Box
          className="floating-navbar-menu"
          position="fixed"
          {...(panelPlacement.top !== undefined ? { top: `${panelPlacement.top}px` } : {})}
          {...(panelPlacement.bottom !== undefined ? { bottom: `${panelPlacement.bottom}px` } : {})}
          left={`${panelPlacement.left}px`}
          width={`${panelPlacement.width}px`}
          zIndex={Z_MENU}
          pointerEvents={isExpanded ? 'auto' : 'none'}
        >
          {/* Expanded Navigation Panel */}
          <Collapse in={isExpanded} animateOpacity>
            <Box
              bg={colorMode === 'light'
                ? "rgba(255, 255, 255, 0.98)"
                : "rgba(10, 10, 15, 0.95)"}
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              border="1px solid"
              borderColor={getColor("border.light", colorMode)}
              p={4}
              // Always fully on screen, scrolling INTERNALLY; overscrollBehavior stops that
              // inner scroll chaining to the page on phones (2026-08-20 rule).
              maxH={menuMaxH(panelPlacement)}
              overflowY="auto"
              overscrollBehavior="contain"
              boxShadow={LAUNCHER.SHADOW_MENU}
              animation={reducedMotion ? undefined : `${slideIn} 0.3s ease-out`}
              css={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: getColor("secondary", colorMode),
                  borderRadius: '2px',
                },
              }}
            >
              {/* Theme Toggle Button - Always visible */}
              {!brandConfig.colorModeLocked && (
<Flex justify="flex-end" mb={3}>
                <Tooltip hasArrow placement="left" openDelay={300} label={colorMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
                  <IconButton
                    aria-label="Toggle color mode"
                    icon={colorMode === 'light' ? <FiMoon /> : <FiSun />}
                    size="md"
                    variant="ghost"
                    color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                    onClick={toggleColorMode}
                    _hover={{
                      bg: colorMode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                      transform: 'scale(1.1)',
                    }}
                  />
                </Tooltip>
              </Flex>
)}

              {/* User Profile Section */}
              {isAuthenticated && user && (
                <Flex
                  align="center"
                  justify="space-between"
                  mb={4}
                  p={3}
                  bg={colorMode === 'light' ? "rgba(90, 200, 250, 0.05)" : "rgba(90, 200, 250, 0.1)"}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'light' ? "rgba(90, 200, 250, 0.1)" : "rgba(90, 200, 250, 0.2)"}
                >
                  <Tooltip hasArrow placement="top" openDelay={300} label="Open My Details">
                    <HStack
                      spacing={3}
                      cursor="pointer"
                      minH={`${LAUNCHER.ROW_MIN_H}px`}
                      onClick={() => handleNavItemClick('/employees/my-details')}
                      _hover={{ opacity: 0.8 }}
                      transition="opacity 0.2s"
                    >
                      <Avatar
                        size="sm"
                        name={`${user.fName || user.firstName || ""} ${user.lName || user.lastName || ""}`}
                        src={user.profilePhoto || undefined}
                        bg={getColor("secondary", colorMode)}
                      />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="600" color={colorMode === 'light' ? "gray.700" : "white"}>
                          {user.fName || user.firstName || 'User'} {user.lName || user.lastName || ''}
                        </Text>
                        <Text fontSize="xs" color={colorMode === 'light' ? "gray.500" : "gray.400"}>
                          {user.email}
                        </Text>
                      </VStack>
                    </HStack>
                  </Tooltip>
                  <Tooltip hasArrow placement="left" openDelay={300} label="Log out of this device">
                    <IconButton
                      aria-label="Logout"
                      icon={<FiLogOut />}
                      size="md"
                      variant="ghost"
                      color="red.400"
                      onClick={handleLogout}
                      _hover={{
                        bg: 'rgba(255, 0, 0, 0.1)',
                        transform: 'scale(1.1)',
                      }}
                    />
                  </Tooltip>
                </Flex>
              )}

              {/* Account links — Security for everyone, Onboarding for ONBOARDING permission. */}
              {isAuthenticated && user && (
                <VStack align="stretch" spacing={1} mb={4}>
                  <Button size="md" minH={`${LAUNCHER.ROW_MIN_H}px`} variant="ghost" justifyContent="flex-start" leftIcon={<FiShield />} onClick={() => handleNavItemClick('/security')}>
                    Security &amp; sign-in
                  </Button>
                  {(user.permissions?.includes('ONBOARDING') || user.permissions?.includes('MASTER_ADMIN')) && (
                    <Button size="md" minH={`${LAUNCHER.ROW_MIN_H}px`} variant="ghost" justifyContent="flex-start" leftIcon={<FiUserPlus />} onClick={() => handleNavItemClick('/onboarding')}>
                      Onboard people
                    </Button>
                  )}
                </VStack>
              )}

              {/* Quick Actions */}
              <HStack mb={4} spacing={2}>
                <Button
                  size="md"
                  minH={`${LAUNCHER.ROW_MIN_H}px`}
                  leftIcon={<FiHome />}
                  bg={getColor("secondary", colorMode)}
                  color="white"
                  fontWeight="600"
                  borderRadius="full"
                  _hover={{
                    bg: getColor("secondaryHover", colorMode),
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.2s"
                  onClick={() => {
                    navigate('/');
                    setIsExpanded(false);
                  }}
                >
                  Home
                </Button>
                {!isAuthenticated && (
                  <Button
                    size="md"
                    minH={`${LAUNCHER.ROW_MIN_H}px`}
                    bg={getColor("secondary", colorMode)}
                    color="white"
                    fontWeight="600"
                    borderRadius="full"
                    onClick={onLoginOpen}
                    _hover={{
                      bg: getColor("secondaryHover", colorMode),
                      transform: 'translateY(-2px)',
                    }}
                    transition="all 0.2s"
                  >
                    Sign In
                  </Button>
                )}
              </HStack>

              {/* Module Categories */}
              {isAuthenticated && (
                <VStack spacing={2} align="stretch">
                  {Object.entries(categorizedModules).map(([category, moduleIds]) => {
                    const categoryModules = availableModules.filter(m =>
                      moduleIds.includes(m.id)
                    );

                    if (categoryModules.length === 0) return null;

                    return (
                      <Box key={category}>
                        <Button
                          size="md"
                          minH={`${LAUNCHER.ROW_MIN_H}px`}
                          variant="ghost"
                          color={colorMode === 'light' ? "gray.600" : "gray.400"}
                          fontSize="sm"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          justifyContent="space-between"
                          w="full"
                          onClick={() => {
                            const newCategory = activeCategory === category ? null : category;
                            setActiveCategory(newCategory);
                          }}
                          _hover={{
                            bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                            color: getColor("secondary", colorMode),
                          }}
                          rightIcon={
                            <Box
                              transform={activeCategory === category ? 'rotate(90deg)' : 'rotate(0)'}
                              transition="transform 0.2s"
                            >
                              <FiChevronRight />
                            </Box>
                          }
                        >
                          {category}
                        </Button>

                        <Collapse in={activeCategory === category} animateOpacity>
                          <VStack spacing={1} align="stretch" mt={2} pl={4}>
                            {categoryModules.map((module) => {
                              const IconComponent = moduleIcons[module.id] || FiGrid;
                              const isModuleExpanded = expandedModules.has(module.id);
                              const visibleNavItems = getVisibleNavItems(module.navigation);

                              // Get the main module path (handle different navigation formats)
                              let mainModulePath = `/module/${module.id}`;
                              if (module.navigation) {
                                if (Array.isArray(module.navigation) && module.navigation[0]) {
                                  mainModulePath = module.navigation[0].path;
                                } else if (!Array.isArray(module.navigation) && typeof module.navigation === 'object' && 'main' in module.navigation) {
                                  mainModulePath = (module.navigation as any).main.path;
                                }
                              } else if (module.routes?.[0]) {
                                mainModulePath = module.routes[0].path;
                              }

                              // Permissions for the module-level button — use the first
                              // nav item's permissions since that's where the click lands.
                              const moduleButtonPerms: string[] =
                                (visibleNavItems[0]?.permissions as string[] | undefined) ?? [];
                              const moduleButtonLevel = visibleNavItems[0]?.permissionLevel as string | undefined;

                              return (
                                <Box key={module.id}>
                                  <HStack
                                    spacing={0}
                                    onMouseEnter={() => setHoveredItem(`module-${module.id}`)}
                                    onMouseLeave={() => setHoveredItem(null)}
                                  >
                                    <Tooltip
                                      label={
                                        devReveal
                                          ? renderPermissionTooltip({
                                              label: module.name,
                                              path: mainModulePath,
                                              moduleId: module.id,
                                              permissionLevel: moduleButtonLevel,
                                              resolved: moduleButtonPerms,
                                            })
                                          : module.name
                                      }
                                      placement="right"
                                      hasArrow
                                      isDisabled={!devReveal}
                                      bg={devReveal ? 'gray.800' : undefined}
                                      color={devReveal ? 'white' : undefined}
                                      px={devReveal ? 3 : undefined}
                                      py={devReveal ? 2 : undefined}
                                    >
                                    <Button
                                      size="md"
                                      minH={`${LAUNCHER.ROW_MIN_H}px`}
                                      variant="ghost"
                                      color={colorMode === 'light' ? "gray.700" : "gray.300"}
                                      flex="1"
                                      justifyContent="flex-start"
                                      leftIcon={<Icon as={IconComponent} />}
                                      onClick={() => {
                                        // Navigate to the main module page
                                        handleNavItemClick(mainModulePath);
                                      }}
                                      position="relative"
                                      _hover={{
                                        bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                        color: getColor("secondary", colorMode),
                                        transform: 'translateX(4px)',
                                        _before: {
                                          opacity: 1,
                                        }
                                      }}
                                      _before={{
                                        content: '""',
                                        position: 'absolute',
                                        left: '-16px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '3px',
                                        height: '70%',
                                        bg: `linear-gradient(180deg, transparent, ${getColor("secondary", colorMode)}, transparent)`,
                                        borderRadius: 'full',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                      }}
                                      transition="all 0.2s"
                                    >
                                      {module.name}
                                    </Button>
                                    </Tooltip>

                                    {/* Open in new tab button - shows on hover */}
                                    <Tooltip hasArrow placement="top" openDelay={300} label={`Open ${module.name} in a new tab`}>
                                      <IconButton
                                        aria-label="Open in new tab"
                                        icon={<FiExternalLink size="14" />}
                                        size="sm"
                                        minW={`${LAUNCHER.ROW_MIN_H}px`}
                                        h={`${LAUNCHER.ROW_MIN_H}px`}
                                        variant="ghost"
                                        color="gray.400"
                                        opacity={hoveredItem === `module-${module.id}` ? 1 : 0}
                                        pointerEvents={hoveredItem === `module-${module.id}` ? 'auto' : 'none'}
                                        onClick={() => handleOpenInNewTab(mainModulePath)}
                                        _hover={{
                                          bg: 'rgba(90, 200, 250, 0.2)',
                                          color: getColor("secondary", colorMode),
                                        }}
                                        transition="all 0.2s"
                                      />
                                    </Tooltip>

                                    {/* Expand/collapse button */}
                                    {visibleNavItems.length > 0 && (
                                      <Tooltip hasArrow placement="top" openDelay={300} label={isModuleExpanded ? `Collapse ${module.name} pages` : `Show ${module.name} pages`}>
                                        <IconButton
                                          aria-label="Expand module"
                                          icon={
                                            <Box
                                              transform={isModuleExpanded ? 'rotate(90deg)' : 'rotate(0)'}
                                              transition="transform 0.2s"
                                            >
                                              <FiChevronRight size="16" />
                                            </Box>
                                          }
                                          size="sm"
                                          minW={`${LAUNCHER.ROW_MIN_H}px`}
                                          h={`${LAUNCHER.ROW_MIN_H}px`}
                                          variant="ghost"
                                          color="gray.400"
                                          onClick={() => {
                                            toggleModuleExpanded(module.id);
                                          }}
                                          _hover={{
                                            bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                            color: getColor("secondary", colorMode),
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </HStack>

                                  {/* Sub-navigation items */}
                                  <Collapse in={isModuleExpanded} animateOpacity>
                                    <VStack spacing={0.5} align="stretch" mt={1} pl={8}>
                                      {visibleNavItems.map((navItem: any) => (
                                        <HStack
                                          key={navItem.path}
                                          spacing={0}
                                          onMouseEnter={() => setHoveredItem(`nav-${navItem.path}`)}
                                          onMouseLeave={() => setHoveredItem(null)}
                                        >
                                          <Tooltip
                                            label={
                                              devReveal
                                                ? renderPermissionTooltip({
                                                    label: navItem.label,
                                                    path: navItem.path,
                                                    moduleId: module.id,
                                                    permissionLevel: navItem.permissionLevel,
                                                    resolved: (navItem.permissions as string[]) ?? [],
                                                  })
                                                : navItem.label
                                            }
                                            placement="right"
                                            hasArrow
                                            isDisabled={!devReveal}
                                            bg={devReveal ? 'gray.800' : undefined}
                                            color={devReveal ? 'white' : undefined}
                                            px={devReveal ? 3 : undefined}
                                            py={devReveal ? 2 : undefined}
                                          >
                                          <Button
                                            size="sm"
                                            minH={`${LAUNCHER.ROW_MIN_H}px`}
                                            variant="ghost"
                                            color={colorMode === 'light' ? "gray.600" : "gray.400"}
                                            flex="1"
                                            justifyContent="flex-start"
                                            leftIcon={navItem.icon ? <Text fontSize="xs">{navItem.icon}</Text> : undefined}
                                            onClick={() => handleNavItemClick(navItem.path)}
                                            fontSize="sm"
                                            fontWeight="400"
                                            _hover={{
                                              bg: 'rgba(90, 200, 250, 0.1)',
                                              color: getColor("secondary", colorMode),
                                              transform: 'translateX(2px)',
                                            }}
                                            transition="all 0.15s"
                                          >
                                            {navItem.label}
                                            {navItem.badge && (
                                              <Badge
                                                ml={1}
                                                colorScheme={navItem.badge.color || 'gray'}
                                                fontSize="2xs"
                                                variant="subtle"
                                              >
                                                {navItem.badge.text}
                                              </Badge>
                                            )}
                                          </Button>
                                          </Tooltip>

                                          {/* Open in new tab button for sub-items */}
                                          <Tooltip hasArrow placement="top" openDelay={300} label={`Open ${navItem.label} in a new tab`}>
                                            <IconButton
                                              aria-label="Open in new tab"
                                              icon={<FiExternalLink size="12" />}
                                              size="sm"
                                              variant="ghost"
                                              color="gray.500"
                                              opacity={hoveredItem === `nav-${navItem.path}` ? 1 : 0}
                                              pointerEvents={hoveredItem === `nav-${navItem.path}` ? 'auto' : 'none'}
                                              onClick={() => handleOpenInNewTab(navItem.path)}
                                              minW={`${LAUNCHER.ROW_MIN_H}px`}
                                              h={`${LAUNCHER.ROW_MIN_H}px`}
                                              _hover={{
                                                bg: 'rgba(90, 200, 250, 0.2)',
                                                color: getColor("secondary", colorMode),
                                              }}
                                              transition="all 0.2s"
                                            />
                                          </Tooltip>
                                        </HStack>
                                      ))}
                                    </VStack>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </VStack>
                        </Collapse>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </Collapse>
        </Box>
      </Portal>

      {/* ═══ Portal B — FLOATING MENU BUTTON (FAB) — pinned at the user-dragged position ═══ */}
      <Portal>
        <Box
          className="floating-navbar-container"
          position="fixed"
          top={`${position.top}px`}
          left={`${position.left}px`}
          zIndex={Z_FAB}
        >
          {/* Hide-to-edge: the USER's own dismiss. Hiding also closes the menu. */}
          <LauncherHideButton
            fabSize={BUTTON_SIZE}
            wasJustDragged={wasJustDragged}
            label="Hide this menu — it becomes a small tab on the left edge, and “↻ Reset floating panels” in the footer brings it back"
            onClick={() => { setIsExpanded(false); setNavHidden(true); }}
          />

          <Tooltip hasArrow placement="right" openDelay={500} label="Navigation menu — drag to move, tap to open">
            <IconButton
              aria-label="Toggle navigation — drag to move, tap to open"
              aria-expanded={isExpanded}
              icon={isExpanded ? <FiX size={24} /> : <FiMenu size={24} />}
              size="lg"
              w={`${BUTTON_SIZE}px`}
              h={`${BUTTON_SIZE}px`}
              minW={`${BUTTON_SIZE}px`}
              borderRadius="full"
              bg={getColor("secondary", colorMode)}
              color="white"
              border="2px solid"
              borderColor="rgba(255, 255, 255, 0.2)"
              {...dragHandlers}
              {...launcherFabMotion(reducedMotion)}
              // The nav FAB's inset highlight rides ON TOP of the standard drop shadow (doc § Shadow).
              boxShadow={`${LAUNCHER.SHADOW}, inset 0 1px 0 rgba(255, 255, 255, 0.3)`}
              onClick={() => {
                if (wasJustDragged()) return; // a drag must not also count as a tap
                setIsExpanded(!isExpanded);
              }}
              _hover={{
                bg: getColor("secondaryHover", colorMode),
                ...(reducedMotion ? {} : { transform: 'scale(1.1) rotate(180deg)' }),
                boxShadow: `${LAUNCHER.SHADOW_HOVER}, inset 0 1px 0 rgba(255, 255, 255, 0.4)`
              }}
              _active={{
                ...(reducedMotion ? {} : { transform: 'scale(0.95)' }),
                boxShadow: `${LAUNCHER.SHADOW}, inset 0 2px 4px rgba(0, 0, 0, 0.2)`
              }}
              position="relative"
            />
          </Tooltip>
        </Box>
      </Portal>

      {/* ===== Portal C — Emergency logout mini-FAB (always visible when authenticated) =====
          Escape hatch for broken auth state: if permissions never load, `user` is null,
          or the menu won't open for any reason, tapping this immediately clears the
          session. Sits directly above the main FAB and tracks its dragged position
          (no drag state or storage key of its own).
          See vault/how-to/draggable-floating-launchers.md → "Emergency logout mini-FAB". */}
      {isAuthenticated && (
        <Portal>
          <Box
            className="floating-navbar-logout"
            position="fixed"
            top={`${Math.max(EDGE_PAD, position.top - 40)}px`}
            left={`${position.left + 14}px`}
            zIndex={Z_LOGOUT}
            // Desktop only, as on PLCC — on a phone it permanently overlaps calendar / roster
            // grids, and the menu's own logout row is always rendered so nobody is locked out.
            display={{ base: "none", md: "block" }}
          >
            <Tooltip
              hasArrow
              placement="right"
              openDelay={300}
              label="Emergency logout — always visible, bypasses the menu"
            >
              <IconButton
                aria-label="Emergency logout (always visible)"
                icon={<FiLogOut />}
                size="sm"
                borderRadius="full"
                bg="red.400"
                color="white"
                border="2px solid"
                borderColor="rgba(255, 255, 255, 0.3)"
                boxShadow={LAUNCHER.SHADOW}
                opacity={0.85}
                onClick={handleLogout}
                _hover={{
                  opacity: 1,
                  bg: 'red.500',
                  ...(reducedMotion ? {} : { transform: 'scale(1.15)' }),
                  boxShadow: LAUNCHER.SHADOW_HOVER,
                }}
                transition="all 0.2s"
              />
            </Tooltip>
          </Box>
        </Portal>
      )}

      {/* Login Modal */}
      <LoginWithSmsModal
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        onLoginSuccess={() => {
          onLoginClose();
          window.location.reload();
        }}
      />
    </>
  );
};
